import { useCallback, useEffect, useReducer } from 'react';

const TYPES = {
  CHANGE_EDITABLE_FILTERS: 'CHANGE_EDITABLE_FILTERS',
  RESET_FILTERS: 'RESET_FILTERS',
  APPLY_FILTERS: 'APPLY_FILTERS',
  DELETE_EDITABLE_FILTER: 'DELETE_EDITABLE_FILTER'
};

const STATUSES = {
  CLEAN: 'CLEAN',
  DIRTY: 'DIRTY',
  SUBMITTED: 'SUBMITTED'
};

const reducer = (state, { type, ...params }) => {
  switch (type) {
    case TYPES.CHANGE_EDITABLE_FILTERS:
      return {
        status: STATUSES.DIRTY,
        filters: state.filters,
        editableFilters: { ...state.editableFilters, ...params.filters }
      };
    case TYPES.DELETE_EDITABLE_FILTER: {
      const updatedEditableFilters = { ...state.editableFilters };
      delete updatedEditableFilters[params.key];
      return {
        status: STATUSES.DIRTY,
        filters: state.filters,
        editableFilters: updatedEditableFilters
      };
    }
    case TYPES.RESET_EDITABLE_FILTERS:
      return {
        status: STATUSES.CLEAN,
        filters: state.filters,
        editableFilters: { ...state.editableFilters, ...params.filters }
      };
    case TYPES.RESET_FILTERS:
      return {
        status: STATUSES.CLEAN,
        filters: params.filters,
        editableFilters: params.filters
      };
    case TYPES.APPLY_FILTERS: {
      const filters = params.filters || state.editableFilters;
      return {
        status: STATUSES.SUBMITTED,
        editableFilters: filters,
        filters
      };
    }
    default:
      throw new Error(`No type defined in useFilterReducer for ${type}`);
  }
};

const defaultOptions = {
  pagination: null,
  persist: 'session'
};

// Bump this when the persisted shape changes, so stale entries are discarded rather than crash.
const STORAGE_VERSION = 1;

const getStorage = persist =>
  persist === 'local' ? localStorage : sessionStorage;

const getPersistedFilters = (id, persist) => {
  if (!id) {
    return null;
  }

  const raw = getStorage(persist).getItem(`filters-${id}`);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (
      parsed.version !== STORAGE_VERSION ||
      !parsed.state ||
      !parsed.state.filters ||
      !parsed.state.editableFilters
    ) {
      return null;
    }

    return parsed.state;
  } catch {
    return null;
  }
};

const useFilterReducer = ({
  id,
  initialFilters,
  options: passedOptions = {}
}) => {
  const options = { ...defaultOptions, ...passedOptions };

  const persistedFilters = getPersistedFilters(id, options.persist);

  const [state, initialDispatch] = useReducer(
    reducer,
    persistedFilters || {
      status: STATUSES.CLEAN,
      filters: initialFilters,
      editableFilters: initialFilters
    }
  );

  const dispatch = useCallback(
    params => {
      const options = { ...defaultOptions, ...passedOptions };
      initialDispatch({ ...params }, options);
    },
    [initialDispatch, passedOptions]
  );

  const updateFilters = filters => {
    dispatch({ type: TYPES.CHANGE_EDITABLE_FILTERS, filters });
  };

  const deleteEditableFilter = key => {
    dispatch({ type: TYPES.DELETE_EDITABLE_FILTER, key });
  };

  const handleApply = () => {
    if (options.pagination) {
      options.pagination.resetPage();
    }

    dispatch({ type: TYPES.APPLY_FILTERS });
  };

  const applyFilters = filters => {
    if (options.pagination) {
      options.pagination.resetPage();
    }

    dispatch({ type: TYPES.APPLY_FILTERS, filters });
  };

  const handleReset = () => {
    if (options.pagination) {
      options.pagination.resetPage();
    }

    dispatch({ type: TYPES.RESET_FILTERS, filters: initialFilters });
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    getStorage(options.persist).setItem(
      `filters-${id}`,
      JSON.stringify({
        version: STORAGE_VERSION,
        updatedAt: new Date().toISOString(),
        state
      })
    );
  }, [id, state, options.persist]);

  return {
    filters: state.editableFilters,
    status: state.status,
    wasRestored: persistedFilters !== null,
    updateFilters,
    deleteEditableFilter,
    form: state.filters,
    handleApply,
    handleReset,
    applyFilters,
    props: {
      status: state.status,
      STATUSES,
      handleApply,
      handleReset
    }
  };
};

export default useFilterReducer;
